/**
 * Cookie Consent Banner
 * GDPR-compliant consent banner for analytics tracking
 *
 * Features:
 * - Shows on first visit (before any tracking)
 * - Stores consent choice in localStorage
 * - Respects "Do Not Track" browser setting
 * - Clear, simple UI
 */

import React, { useState, useEffect } from 'react';
import { getStoredConsent, updateConsent } from '../../services/analytics';
import { onConsentChange as hyperdxConsentChange } from '../../services/observability';
import './CookieConsentBanner.css';

interface CookieConsentBannerProps {
  onConsentChange?: (granted: boolean) => void;
}

const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({ onConsentChange }) => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if consent has already been given
    const storedConsent = getStoredConsent();

    // Only show banner if no consent decision has been made
    if (storedConsent === null) {
      // Small delay to prevent flash on page load
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    updateConsent(true);
    hyperdxConsentChange(true); // Update HyperDX consent
    setShowBanner(false);
    onConsentChange?.(true);
  };

  const handleDecline = () => {
    updateConsent(false);
    hyperdxConsentChange(false); // Update HyperDX consent
    setShowBanner(false);
    onConsentChange?.(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="cookie-consent-banner" role="dialog" aria-labelledby="cookie-consent-title">
      <div className="cookie-consent-content">
        <div className="cookie-consent-text">
          <h3 id="cookie-consent-title" className="cookie-consent-heading">
            Cookie Settings
          </h3>
          <p className="cookie-consent-description">
            We use analytics cookies to understand how you use our platform and improve your experience.
            Your data is never sold and is only used for product improvement.
          </p>
        </div>

        <div className="cookie-consent-actions">
          <button
            onClick={handleDecline}
            className="cookie-consent-button cookie-consent-decline"
            aria-label="Decline analytics cookies"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            className="cookie-consent-button cookie-consent-accept"
            aria-label="Accept analytics cookies"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
